<script setup lang="ts">
const props = defineProps<{
  close: () => void;
}>();

const { trackEvent } = useGAnalytics();
const { toggleBlockScroll } = useBlockScroll();
const appStateStore = useAppStateStore();
const userStore = useUserStore();
// ...removed openGameNavs, dropdown logic
const searchTerm = ref<string>("");
const filterGames = computed(() => {
  return appStateStore.merchants.filter((game) => {
    return game.id.toLowerCase().includes(searchTerm.value.toLowerCase());
  });
});

/*const toggleOpenGameSubMenu = (id: string) => {
  if (openGameNavs.value.has(id)) {
    openGameNavs.value.delete(id);
  } else {
    openGameNavs.value.add(id);
  }
  openGameNavs.value = new Set(openGameNavs.value);
};*/

const storeSections = [
  {
    translationId: "gameNav.consumables",
    path: "store",
  },
  {
    translationId: "gameNav.market",
    path: "market",
  },
  {
    translationId: "appMenu.collection",
    path: "collection",
  },
];

const getActiveMarketRoutes = computed(() => (id: MerchantId) => {
  const activePages = appStateStore.getMerchantActivePages(id);
  if (!activePages) {
    return storeSections;
  }
  let filtered = storeSections.filter((route) =>
    activePages.includes(route.path)
  );
  if (!filtered.some((route) => route.path === "collection")) {
    const collectionRoute = storeSections.find(
      (route) => route.path === "collection"
    );
    if (collectionRoute) filtered.push(collectionRoute);
  }
  return filtered;
});

const hoveredIndex = ref<number>();
const setHoveredIndex = (index: number) => {
  hoveredIndex.value = index;
};
const clearHoveredIndex = () => {
  hoveredIndex.value = undefined;
};

const trackAndSetMerchant = (path: string, game: string) => {
  trackEvent(`nav_games_${path}_${game}`);
  appStateStore.currentMerchant = game as MerchantId;
};

const linkToGame = (game: Merchant) => {
  if (game.status === "COMING_SOON") return undefined;
  const activePages = appStateStore.getMerchantActivePages(game.id);
  return `/store/${game.id}`;
  /*if (activePages?.includes("store")) return `/store/${game.id}`;
  else if (activePages?.includes("market")) return `/market/${game.id}`;
  else if (activePages?.includes("craft")) return `/craft/${game.id}`;
  return `/market/${game.id}`;*/
};

onMounted(() => {
  toggleBlockScroll(true);
});
</script>
<template>
  <div class="md:hidden flex justify-between items-start pt-8 px-6 pb-6">
    <div class="flex flex-col gap-2">
      <p class="text-2.5xl font-bold text-neutral-200">
        {{ $t("main.gameNav.ourGames") }}
      </p>
      <p class="text-md text-neutral-300">
        {{ $t("main.gameNav.selectGame") }}
      </p>
    </div>
    <NuxtIcon
      name="mdi:close"
      :alt="$t('main.imageAltTexts.closeIcon')"
      class="w-9 h-9 text-neutral-300 cursor-pointer hover:text-neutral-200 transition-all duration-300"
      @click="close" />
  </div>
  <div
    v-for="game in appStateStore.merchants.filter(
      (game) =>
        game.status !== 'COMING_SOON' &&
        (game.id !== 'elemental-raiders' || userStore.elementalRaidersPlayer)
    )"
    :key="game.id"
    class="md:hidden px-6">
    <NuxtLinkLocale
      :to="linkToGame(game)"
      @click="
        trackAndSetMerchant('market', game.id);
        close();
      "
      :data-test="'link-nav-' + game.id">
      <img
        :src="getGameThumbnail(game.id)"
        :alt="game.id"
        class="w-full object-cover rounded-md transition-transform duration-500 ease-in-out transform"
        :class="
          game.status === 'COMING_SOON'
            ? 'opacity-50 saturate-50'
            : 'cursor-pointer'
        " />
    </NuxtLinkLocale>
    <div :ref="game.id" class="w-full flex flex-col pt-2 text-neutral-200">
      <div class="flex items-center gap-2">
        <img
          :src="getGameIcon(game.id)"
          :alt="game.id"
          class="w-10 h-10 object-cover rounded-lg transition-transform duration-500 ease-in-out transform" />
        <p class="md:hidden text-xl font-bold">
          {{ $t(`main.merchant.${game.id}`) }}
        </p>
      </div>
      <div
        class="w-full font-normal text-md flex flex-col justify-center gap-4 px-0 pt-2 pb-4 transition-all duration-300 md:rounded-b-xl">
        <div class="flex items-center gap-2">
          <template
            v-for="(section, index) in getActiveMarketRoutes(game.id)"
            :key="$t(`main.${section.translationId}`)">
            <NuxtLinkLocale
              :to="
                game.status === 'COMING_SOON'
                  ? undefined
                  : `/${section.path}/${game.id}`
              "
              class="transition-all duration-300"
              :class="[
                $route.path?.includes(`${section.path}`) &&
                $route.params.merchant === game.id
                  ? 'text-neutral-200'
                  : 'text-neutral-300',
                ,
                game.status === 'COMING_SOON'
                  ? 'pointer-events-none opacity-50'
                  : 'hover:text-neutral-200',
              ]"
              @click="trackAndSetMerchant(section.path, game.id), close()"
              @mouseover="setHoveredIndex(index)"
              @mouseleave="clearHoveredIndex">
              <p class="font-semibold text-nowrap">
                {{ $t(`main.${section.translationId}`) }}
              </p>
            </NuxtLinkLocale>
            <span
              v-if="index < getActiveMarketRoutes(game.id).length - 1"
              :class="
                game.status === 'COMING_SOON'
                  ? 'text-neutral-300/50'
                  : 'text-neutral-300'
              "
              >|</span
            >
          </template>
        </div>
      </div>
    </div>
  </div>
  <div class="hidden md:block md:px-6">
    <div class="flex justify-between items-start pt-8">
      <div class="flex flex-col gap-2">
        <p class="text-xl font-bold text-neutral-200">
          {{ $t("main.gameNav.ourGames") }}
        </p>
        <p class="text-xs text-neutral-300">
          {{ $t("main.gameNav.selectGame") }}
        </p>
      </div>
      <NuxtIcon
        name="mdi:close"
        :alt="$t('main.imageAltTexts.closeIcon')"
        class="w-9 h-9 text-neutral-300 cursor-pointer hover:text-neutral-200 transition-all duration-300"
        @click="close" />
    </div>
    <!--<div class="my-6">
      <form
        class="relative h-[42px] rounded-lg bg-primary-900 flex items-center w-full"
        @submit.prevent="$emit('doSearch')">
        <NuxtIcon
          name="mdi:search"
          class="absolute left-4 h-5 w-5 shrink-0 text-neutral-300"
          aria-hidden="true" />
        <input
          v-model="searchTerm"
          class="pl-12 text-neutral-300 placeholder:text-neutral-300 w-full h-full px-4 rounded-lg bg-inherit border-primary-500 border-r-0 focus:ring-transparent focus:border-primary-500"
          type="text"
          placeholder="Search"
          autocomplete="off"
          data-1p-ignore
          data-lpignore="true"
          data-test="input-filter-search" />
      </form>
    </div>-->
    <div v-if="filterGames.length === 0" class="mt-4 w-[296px]">
      <p class="text-neutral-300">{{ $t("main.gameNav.noGamesfound") }}</p>
    </div>
    <div v-else class="mt-4 flex flex-col gap-6">
      <template v-for="game in filterGames" :key="game.id">
        <div
          v-if="
            game.id !== 'elemental-raiders' || userStore.elementalRaidersPlayer
          "
          :id="game.id + '-home'"
          class="relative rounded-sm flex flex-col gap-1"
          @click="game.status === 'COMING_SOON' ? null : close()">
          <NuxtLinkLocale
            :to="linkToGame(game)"
            @click="trackAndSetMerchant('market', game.id)"
            :data-test="'link-nav-' + game.id">
            <img
              :src="getGameThumbnail(game.id)"
              :alt="game.id"
              class="w-[296px] h-[149px] object-cover rounded-xs transition-transform duration-500 ease-in-out transform"
              :class="
                game.status === 'COMING_SOON'
                  ? 'opacity-50 saturate-50'
                  : 'cursor-pointer'
              " />
          </NuxtLinkLocale>
          <div
            v-if="game.status === 'COMING_SOON'"
            class="absolute top-4 right-4 z-10 w-fit bg-primary-500 px-2.5 py-0.5 text-neutral-200 text-[10px] font-semibold rounded-xl">
            {{ $t("main.home.comingSoon") }}
          </div>
          <div>
            <p
              class="font-semibold z-10 text-neutral-200"
              :class="
                game.status === 'COMING_SOON' ? 'text-neutral-200/50' : null
              ">
              {{ $t(`main.merchant.${game.id}`) }}
            </p>
            <div class="flex items-center gap-2">
              <template
                v-for="(section, index) in getActiveMarketRoutes(game.id)"
                :key="$t(`main.${section.translationId}`)">
                <NuxtLinkLocale
                  :to="
                    game.status === 'COMING_SOON'
                      ? undefined
                      : `/${section.path}/${game.id}`
                  "
                  class="transition-all duration-300"
                  :class="[
                    $route.path?.includes(`${section.path}`) &&
                    $route.params.merchant === game.id
                      ? 'text-neutral-200'
                      : 'text-neutral-300',
                    ,
                    game.status === 'COMING_SOON'
                      ? 'pointer-events-none opacity-50'
                      : 'hover:text-neutral-200',
                  ]"
                  @click="trackAndSetMerchant(section.path, game.id), close()"
                  @mouseover="setHoveredIndex(index)"
                  @mouseleave="clearHoveredIndex">
                  <p class="text-xs font-semibold text-nowrap uppercase">
                    {{ $t(`main.${section.translationId}`) }}
                  </p>
                </NuxtLinkLocale>
                <span
                  v-if="index < 2 && getActiveMarketRoutes(game.id).length > 1"
                  :class="
                    game.status === 'COMING_SOON'
                      ? 'text-neutral-300/50'
                      : 'text-neutral-300'
                  "
                  >|</span
                >
              </template>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
